import React, { useCallback, useState } from 'react';
import * as yup from 'yup';
import { SxStyle } from '../../../../../types';
import { useStringsContext } from '../../../../../contexts/strings';
import AccordionWrapper from '../accordion';
import svStrings from '../../../../../sv-strings';
import { InputWithValidateProps } from '../../../../common/input/text-input-with-validate';
import { PushNotificationValues, PushSendType } from '../../push-notification-send';
import Stack from '@mui/material/Stack';
import SelectWithValidate from '../../../../common/input/select-with-validate';
import DateTimeWithValidate from '../../../../common/input/datetime-with-validate';
import { Typography } from '@mui/material';
import { SchedulePushdNotificationBody } from '../../../../../types/notification';
import moment from 'moment';

const sendKey = 'send';
const scheduleOptionsText = [svStrings.pushFields.sendImmediately, svStrings.pushFields.sendLater];
const scheduledTimeKey: keyof SchedulePushdNotificationBody = 'scheduledTime';

const dateValidation = yup
  .date()
  .min(new Date(), svStrings.validations.validDateInPast)
  .required(svStrings.validations.isRequired)
  .typeError(svStrings.validations.validDate);

const styles: SxStyle = {
  container: {
    paddingLeft: '15px',
    paddingRight: '15px',
    paddingBottom: '24px',
  },
};

interface Props extends InputWithValidateProps {
  expanded: boolean;
  pushNotification: PushNotificationValues;
  onSendSelection: (value: PushSendType) => void;
}

const PushNotificationSchedule = ({
  expanded,
  pushNotification,
  onSendSelection,
  onValue,
  onRemoveValue,
  onValidation,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();
  const [selectedSend, setSelectedSend] = useState<PushSendType>(PushSendType.Now);

  const onSendValue = useCallback(
    (key: string, value: PushSendType) => {
      setSelectedSend(value);
      if (value === PushSendType.Now) {
        onRemoveValue?.(scheduledTimeKey);
      }
      onSendSelection(value);
    },
    [onRemoveValue, onSendSelection]
  );

  const handleOnDateTimeValue = useCallback(
    (key: string, value: string) => onValue?.(key, moment(value).format()),
    [onValue]
  );

  return (
    <AccordionWrapper expanded={expanded} name={strings.pushFields.titleSchedule}>
      <Stack spacing={3} sx={styles.container}>
        <SelectWithValidate
          name={sendKey}
          options={Object.values(PushSendType)}
          optionsText={scheduleOptionsText}
          label={strings.pushFields.scheduleTitle}
          value={selectedSend}
          onValue={(key, value) => onSendValue(key, value as PushSendType)}
        />
        {selectedSend === PushSendType.Scheduled ? (
          <DateTimeWithValidate<SchedulePushdNotificationBody>
            name={scheduledTimeKey}
            label={strings.pushFields.dateTitle}
            value={''}
            validation={dateValidation}
            onValue={handleOnDateTimeValue}
            onValidation={onValidation}
            hideValidationError={hideValidationError}
          />
        ) : (
          <Typography textAlign="center" variant="caption">
            {strings.pushNotifications.sentWithinMin}
          </Typography>
        )}
      </Stack>
    </AccordionWrapper>
  );
};

export default PushNotificationSchedule;
